import type {RouteLocationNormalized, RouteLocationRaw, Router} from 'vue-router';
import {FLEETWIRE_MEASUREMENT_ID, resolveSiteArea} from '@/plugins/gtag';

export type NavSource =
    | 'sidebar'
    | 'sidebar-bottom'
    | 'command-palette'
    | 'in-app'
    | 'direct';

/** Sections we ignore for host IA telemetry (auth, public, etc.). */
const IGNORED_SECTIONS = new Set([
    'login',
    'register',
    'password',
    'email', // /email/verify — not the /emails dashboard section
    'verify',
    'stripe',
    'braintree',
    'square',
    'public',
    'rental',
    'invoice',
    'pay',
    'signing',
]);

let pendingNavSource: NavSource | null = null;

/**
 * Mark the next route transition as coming from a known chrome control
 * (sidebar, command palette, etc.). Cleared when consumed by afterEach.
 */
export function markNavIntent(source: NavSource): void {
    pendingNavSource = source;
}

/** Test helper — reset module state between Vitest cases. */
export function resetNavTelemetryState(): void {
    pendingNavSource = null;
}

export function resolveNavSection(path: string): string {
    const firstSegment = path.split('?')[0].split('/').filter(Boolean)[0] ?? 'root';

    return firstSegment || 'root';
}

/** Best-effort section from a vue-router location (path preferred, else name prefix). */
export function resolveNavSectionFromLocation(location: RouteLocationRaw): string {
    if (typeof location === 'string') {
        return resolveNavSection(location);
    }

    if ('path' in location && typeof location.path === 'string' && location.path.length > 0) {
        return resolveNavSection(location.path);
    }

    if ('name' in location && typeof location.name === 'string' && location.name.length > 0) {
        return location.name.split('.')[0] ?? location.name;
    }

    return 'unknown';
}

export function consumeNavSource(fromHasName: boolean): NavSource {
    if (pendingNavSource) {
        const source = pendingNavSource;
        pendingNavSource = null;

        return source;
    }

    // First SPA navigation (hard load / bookmark / typed URL).
    if (!fromHasName) {
        return 'direct';
    }

    return 'in-app';
}

function shouldTrackSectionChange(to: RouteLocationNormalized, from: RouteLocationNormalized): boolean {
    if (resolveSiteArea(to.path) !== 'dashboard') {
        return false;
    }

    const toSection = resolveNavSection(to.path);
    const fromSection = resolveNavSection(from.path);

    if (IGNORED_SECTIONS.has(toSection) || IGNORED_SECTIONS.has(fromSection)) {
        return false;
    }

    // Lightweight: only section-to-section hops (not within-section detail drilldowns).
    if (from.name && toSection === fromSection) {
        return false;
    }

    return true;
}

function trackGtagEvent(eventName: string, data: Record<string, string | undefined>): void {
    if (typeof window === 'undefined') {
        return;
    }

    const params = Object.fromEntries(
        Object.entries(data).filter(([, value]) => value !== undefined && value !== null),
    ) as Record<string, string>;

    const payload = {...params, send_to: FLEETWIRE_MEASUREMENT_ID};

    if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, payload);
        return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(['event', eventName, payload]);
}

/**
 * Install dashboard nav-path telemetry on the router.
 * Emits `nav_section_change` with from/to section + how the user arrived.
 */
export function installNavTelemetry(router: Router): void {
    router.afterEach((to, from) => {
        // Always consume a pending intent so it cannot leak onto a later hop.
        const navSource = consumeNavSource(Boolean(from.name));

        if (to.path === from.path) {
            return;
        }

        if (!shouldTrackSectionChange(to, from)) {
            return;
        }

        const fromSection = resolveNavSection(from.path);
        const toSection = resolveNavSection(to.path);

        trackGtagEvent('nav_section_change', {
            from_section: from.name ? fromSection : '(entry)',
            to_section: toSection,
            nav_source: navSource,
            from_route: typeof from.name === 'string' ? from.name : undefined,
            to_route: typeof to.name === 'string' ? to.name : undefined,
        });
    });
}
